import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './Menu.css'

/**
 * Dropdown popover anchored to a trigger. Used by the header account menu, the
 * page-header kebabs and table row actions.
 *
 * The panel is PORTALLED to <body> and fixed-positioned, because every surface it
 * opens from clips: the page header rounds its corners over an accent hairline,
 * and the DataTable clips its own rounded corners over a horizontal scroll area.
 * Both of those are `overflow: hidden`, and an absolutely-positioned panel inside
 * them simply lost its bottom half — 42px of a 58px menu on the groups page.
 * Anchoring in script is the only fix that cannot be reintroduced by a future
 * `overflow` somewhere up the tree.
 *
 * `children` may be a render function; it then receives `{ close }` and the panel
 * stops closing on its own, which is what a panel holding a form needs.
 *
 * @param {(props: {open: boolean, toggle: () => void}) => ReactNode} trigger
 * @param {'start'|'end'} [align]  which inline edge of the trigger the panel hangs from
 * @param {string} [width]         panel width, defaults to a comfortable menu width
 */
export default function Menu({ trigger, align = 'end', width, className = '', children }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const rootRef = useRef(null)
  const panelRef = useRef(null)
  const id = useId()

  const place = useCallback(() => {
    const anchor = rootRef.current
    const panel = panelRef.current
    if (!anchor || !panel) return

    const a = anchor.getBoundingClientRect()
    const p = panel.getBoundingClientRect()
    const gap = 8
    const margin = 8

    // Flip above the trigger when the panel would run off the bottom.
    const below = a.bottom + gap + p.height <= window.innerHeight - margin
    const top = below ? a.bottom + gap : Math.max(margin, a.top - gap - p.height)

    // `align` is expressed in logical terms; resolve it against the document's
    // direction so one prop works in both RTL and LTR.
    const rtl = getComputedStyle(document.documentElement).direction === 'rtl'
    const startEdge = rtl ? a.right - p.width : a.left
    const endEdge = rtl ? a.left : a.right - p.width
    const left = Math.min(
      Math.max(margin, align === 'start' ? startEdge : endEdge),
      window.innerWidth - p.width - margin,
    )

    setPos({ top: Math.round(top), left: Math.round(left) })
  }, [align])

  // Measure and place before paint, so the panel never appears at 0,0 first.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return undefined
    }
    place()
    const onMove = () => place()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, place])

  useEffect(() => {
    if (!open) return undefined

    const onPointerDown = (event) => {
      // The panel is no longer a DOM child of the trigger, so both have to be
      // consulted before treating a click as "outside".
      if (rootRef.current?.contains(event.target)) return
      if (panelRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      // Put the caret back on the trigger, or the next Tab restarts from the top
      // of the document.
      rootRef.current?.querySelector('button')?.focus()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const isPanel = typeof children === 'function'
  const close = () => setOpen(false)

  return (
    <div className={`menu ${className}`.trim()} ref={rootRef}>
      {trigger({ open, toggle: () => setOpen((value) => !value), id })}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={id}
            className="menu__panel"
            style={{
              width,
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              // Hidden for the one frame between mount and measurement.
              visibility: pos ? 'visible' : 'hidden',
            }}
            /* Deliberately not role="menu": the children are real buttons and links,
               so native Tab already works. Declaring the ARIA menu role without the
               arrow-key contract strands screen-reader users in application mode. */
            role={isPanel ? 'dialog' : undefined}
            onClick={isPanel ? undefined : close}
            data-testid="menu"
          >
            {isPanel ? children({ close }) : children}
          </div>,
          document.body,
        )}
    </div>
  )
}

/**
 * A row inside the panel. Defaults to a button; pass `as={Link}` or `as="a"` when
 * the row navigates, so this file stays independent of the router.
 */
export function MenuItem({ as: Tag = 'button', icon: Icon, danger = false, tone, children, ...rest }) {
  return (
    <Tag
      className={`menu__item${danger ? ' menu__item--danger' : ''}`}
      {...(Tag === 'button' ? { type: 'button' } : null)}
      {...rest}
      data-testid="menu-item"
    >
      {Icon && (
        <Icon
          className="menu__item-icon"
          /* The label carries the meaning here, so only the glyph takes the tone —
             a whole row in colour would read as a state, not as a choice. */
          data-tone={tone}
          size={16}
          strokeWidth={1.75}
        />
      )}
      <bdi>{children}</bdi>
    </Tag>
  )
}

export function MenuSeparator() {
  return <div className="menu__separator" role="separator" />
}
